import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e4e7eb",
        color: theme.palette.common.black,
        fontSize: 12,
        fontWeight: 600
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

// Formatowanie Term/Period jak w kalkulatorze cen
function formatBillingPlan(billingPlan) {
    if (!billingPlan || (!billingPlan.term && !billingPlan.invoicePeriod)) {
        return 'perpetual';
    }

    const term = billingPlan.term || 1;
    const invoicePeriod = billingPlan.invoicePeriod || 1;

    if (term === 1 && invoicePeriod === 1) {
        return 'M/M';
    } else if (term === 12 && invoicePeriod === 1) {
        return 'R/M';
    } else if (term === 12 && invoicePeriod === 12) {
        return 'R/R';
    } else {
        return `${term}/${invoicePeriod}`;
    }
}

// Chip dla Type zamówień
function TypeChip({ type }) {
    if (!type) return null;
    const t = String(type).toLowerCase().trim();

    let bg = '#e0e0e0';
    let fg = '#555';

    switch (t) {
        case 'trial':
        case 'demo':
            bg = '#ff9800'; fg = '#fff'; break;
        case 'purchase':
        case 'subscription':
            bg = '#2e7d32'; fg = '#fff'; break;
        case 'upgrade':
        case 'renewal':
            bg = '#1976d2'; fg = '#fff'; break;
        case 'downgrade':
            bg = '#f57c00'; fg = '#fff'; break;
        case 'cancel':
        case 'termination':
            bg = '#d32f2f'; fg = '#fff'; break;
        default:
            const hash = t.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
            const hue = Math.abs(hash) % 360;
            bg = `hsl(${hue}, 60%, 45%)`; fg = '#fff'; break;
    }

    return (
        <Chip
            label={type}
            size="small"
            sx={{
                fontSize: 10,
                fontWeight: 600,
                height: 20,
                minWidth: 50,
                '& .MuiChip-label': { px: 0.5 },
                backgroundColor: bg,
                color: fg,
                borderRadius: '10px',
            }}
        />
    );
}

// Chip dla Status zamówień
function StatusChip({ status }) {
    if (!status) return null;
    const s = String(status).toLowerCase().trim();

    let bg = '#e0e0e0';
    let fg = '#555';

    switch (s) {
        case 'completed':
        case 'paid':
            bg = '#2e7d32'; fg = '#fff'; break;
        case 'pending':
        case 'processing':
            bg = '#ff9800'; fg = '#fff'; break;
        case 'cancelled':
        case 'failed':
        case 'refunded':
            bg = '#d32f2f'; fg = '#fff'; break;
        case 'draft':
            bg = '#757575'; fg = '#fff'; break;
        default:
            const hash = s.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
            const hue = Math.abs(hash) % 360;
            bg = `hsl(${hue}, 60%, 45%)`; fg = '#fff'; break;
    }

    return (
        <Chip
            label={status}
            size="small"
            sx={{
                fontSize: 10,
                fontWeight: 600,
                height: 20,
                minWidth: 60,
                '& .MuiChip-label': { px: 0.5 },
                backgroundColor: bg,
                color: fg,
                borderRadius: '10px',
            }}
        />
    );
}

function Row(props) {
    const { order } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <StyledTableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell>{order.id}</StyledTableCell>
                <StyledTableCell>{order.CustomerName}</StyledTableCell>
                <StyledTableCell>{order.customerId}</StyledTableCell>
                <StyledTableCell sx={{ padding: '4px 8px' }}>
                    <TypeChip type={order.type} />
                </StyledTableCell>
                <StyledTableCell sx={{ padding: '4px 8px' }}>
                    <StatusChip status={order.status} />
                </StyledTableCell>
                <StyledTableCell>{formatBillingPlan(order.billingPlan)}</StyledTableCell>
                <StyledTableCell>{order.invoiceNumber}</StyledTableCell>
                <StyledTableCell align="right">{order.createdAt}</StyledTableCell>
                <StyledTableCell align="right">{order.completedAt}</StyledTableCell>
            </TableRow>
            <TableRow>
                <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Szczegóły pozycji zamówienia
                            </Typography>
                            <Table size="small" aria-label="items">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>ProductId</StyledTableCell>
                                        <StyledTableCell>ParentProductId</StyledTableCell>
                                        <StyledTableCell>ProductName</StyledTableCell>
                                        <StyledTableCell>ProductPartNumber</StyledTableCell>
                                        <StyledTableCell>ProductGroup</StyledTableCell>
                                        <StyledTableCell>SubscriptionId</StyledTableCell>
                                        <StyledTableCell>ParentSubscriptionId</StyledTableCell>
                                        <StyledTableCell align="right">Quantity</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.items?.map((item, index) => (
                                        <TableRow key={`${item.productId}-${index}`}>
                                            <StyledTableCell component="th" scope="row">{item.productId}</StyledTableCell>
                                            <StyledTableCell>{item.parentProductId}</StyledTableCell>
                                            <StyledTableCell>{item.ProductName}</StyledTableCell>
                                            <StyledTableCell>{item.productPartNumber}</StyledTableCell>
                                            <StyledTableCell>{item.ProductGroup}</StyledTableCell>
                                            <StyledTableCell>{item.subscriptionId}</StyledTableCell>
                                            <StyledTableCell>{item.parentSubscriptionId}</StyledTableCell>
                                            <StyledTableCell align="right">{item.quantity}</StyledTableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </StyledTableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function Orders() {
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');

    React.useEffect(() => {
        (async () => {
            try {
                const response = await axios.get("ab/getorderslimit/100/");
                setOrders(response.data || []);
            } catch (error) {
                console.log(error);
                if (error?.response?.status === 401) {
                    alert("Your access is not allowed.");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const navigate = useNavigate();
    const routeChange = () => {
        let path = "/orders";
        navigate(path);
    };

    // Wyszukiwarka po kluczowych polach
    const filteredOrders = React.useMemo(() => {
        if (!search) return orders;
        const q = search.toLowerCase();
        return orders.filter(o => {
            return (
                (o.CustomerName && o.CustomerName.toLowerCase().includes(q)) ||
                (o.customerId && String(o.customerId).includes(q)) ||
                (o.type && o.type.toLowerCase().includes(q)) ||
                (o.status && o.status.toLowerCase().includes(q)) ||
                (o.ProductName && o.ProductName.toLowerCase().includes(q)) ||
                (String(o.id || '').toLowerCase().includes(q)) ||
                (o.invoiceNumber && o.invoiceNumber.toLowerCase().includes(q))
            );
        });
    }, [orders, search]);

    const renderOrdersTable = (ordersToRender) => {
        return (
            <React.Fragment>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Title>Zamówienia</Title>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                size="small"
                                variant="outlined"
                                label="Szukaj zamówienia"
                                placeholder="Klient, typ, status, faktura..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Stack>
                    </Box>

                    <TableContainer>
                        <Table size="small" aria-label="collapsible table" padding="checkbox">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell />
                                    <StyledTableCell>Id</StyledTableCell>
                                    <StyledTableCell>CustomerName</StyledTableCell>
                                    <StyledTableCell>CustomerId</StyledTableCell>
                                    <StyledTableCell>Type</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Term/Period</StyledTableCell>
                                    <StyledTableCell>Invoice number</StyledTableCell>
                                    <StyledTableCell align="right">Created at</StyledTableCell>
                                    <StyledTableCell align="right">Completed at</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ordersToRender.map((order) => (
                                    <Row key={order.id} order={order} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {ordersToRender.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Brak zamówień spełniających kryteria wyszukiwania.
                            </Typography>
                        </Box>
                    )}

                    <Link color="primary" href="#" onClick={routeChange} sx={{ mt: 3 }}>
                        Wczytaj kolejne
                    </Link>
                </Paper>
            </React.Fragment>
        );
    };

    const contents = loading
        ? (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <p><em>&nbsp;&nbsp;Loading... trwa ładowanie danych z AB...</em></p>
            </Box>
        )
        : renderOrdersTable(filteredOrders);

    return (
        <>
            {contents}
        </>
    );
}
